import { SkillVisibility, SkillWritePayload } from "@models";
import { Button, Stack, Typography } from "@mui/material";
import { LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  canCreateInternalSkill,
  getSkillVisibilityOptions,
  isAdminUser,
  isSkillNameKebabCase,
  isSkillVisibility,
  SKILL_NAME_KEBAB_CASE_ERROR,
  SKILL_NAME_KEBAB_CASE_HELPER_TEXT,
} from "src/components/Skills/skill-utils";
import {
  SkillFormFields,
  SkillFormValues,
} from "src/components/Skills/SkillFormModal";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import {
  useCreateSkillMutation,
  useDuplicateSkillMutation,
  useGetSkill,
  useGetUser,
  usePatchSkillMutation,
} from "src/hooks";
import { useCurrentAgentThread } from "src/hooks/useAgentThreads";
import { useStore } from "src/stores";
import type { SkillDraftCardArgs, SkillDraftOperation } from "src/types";
import { fillRouteWithSlugs } from "src/utils";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { ToolCallFrame } from "./ToolCallFrame";

type DraftSkillValues = SkillFormValues;
type SkillDraftNamePolicy = NonNullable<SkillDraftCardArgs["namePolicy"]>;

type DraftSkillConfig = {
  operation: SkillDraftOperation;
  sourceSkillId?: string;
  namePolicy: SkillDraftNamePolicy;
};

interface DraftSkillToolProps {
  args: Record<string, any>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
}

const skillDraftOperations: SkillDraftOperation[] = [
  "create",
  "edit",
  "duplicate",
];
const skillDraftNamePolicies: SkillDraftNamePolicy[] = ["kebab", "nonempty"];

const normalizeVisibility = (
  value: unknown,
  allowedVisibilities: SkillVisibility[]
): SkillVisibility => {
  if (isSkillVisibility(value) && allowedVisibilities.includes(value)) {
    return value;
  }

  return allowedVisibilities[0] ?? "PRIVATE";
};

const normalizeOperation = (value: unknown): SkillDraftOperation =>
  skillDraftOperations.includes(value as SkillDraftOperation)
    ? (value as SkillDraftOperation)
    : "create";

const normalizeNamePolicy = (value: unknown): SkillDraftNamePolicy =>
  skillDraftNamePolicies.includes(value as SkillDraftNamePolicy)
    ? (value as SkillDraftNamePolicy)
    : "kebab";

const getDraftValuesFromArgs = (
  args: Record<string, any>,
  allowedVisibilities: SkillVisibility[]
): DraftSkillValues => ({
  name: typeof args.name === "string" ? args.name : "",
  descriptionMd:
    typeof args.descriptionMd === "string" ? args.descriptionMd : "",
  contentMd: typeof args.contentMd === "string" ? args.contentMd : "",
  visibility: normalizeVisibility(args.visibility, allowedVisibilities),
});

const getDraftConfigFromArgs = (
  args: Record<string, any>
): DraftSkillConfig => {
  const sourceSkillId =
    typeof args.sourceSkillId === "string" && args.sourceSkillId.trim()
      ? args.sourceSkillId.trim()
      : undefined;

  return {
    operation: normalizeOperation(args.operation),
    sourceSkillId,
    namePolicy: normalizeNamePolicy(args.namePolicy),
  };
};

const getValidationError = (
  values: DraftSkillValues,
  config: DraftSkillConfig
) => {
  if (!values.name.trim()) {
    return "Name is required.";
  }
  if (!isSkillNameKebabCase(values.name)) {
    return SKILL_NAME_KEBAB_CASE_ERROR;
  }
  if (
    (config.operation === "edit" || config.operation === "duplicate") &&
    !config.sourceSkillId
  ) {
    return "This draft is missing the source skill.";
  }
  if (!values.descriptionMd.trim()) {
    return "Description is required.";
  }
  if (!values.contentMd.trim()) {
    return "Markdown content is required.";
  }
  return null;
};

const requiresSourceSkill = (operation: SkillDraftOperation) =>
  operation === "edit" || operation === "duplicate";

const titleByOperation: Record<SkillDraftOperation, string> = {
  create: "Draft skill",
  edit: "Edit skill",
  duplicate: "Duplicate skill",
};

const getAppliedToolCallIds = (metadata: Record<string, any> | undefined) => {
  return [
    ...new Set(
      [
        ...(Array.isArray(metadata?.applied_tool_call_ids)
          ? metadata.applied_tool_call_ids
          : []),
        ...(Array.isArray(metadata?.appliedToolCallIds)
          ? metadata.appliedToolCallIds
          : []),
      ].map(String)
    ),
  ];
};

// react-doctor-disable-next-line react-doctor/no-giant-component -- The tool card is intentionally kept together so streamed tool state and apply actions stay co-located.
const DraftSkillTool = ({
  args,
  toolResult,
  isLoading = false,
}: DraftSkillToolProps) => {
  const { orgId } = useChatDeviceContext();
  const navigate = useNavigate();
  const createSkill = useCreateSkillMutation();
  const patchSkill = usePatchSkillMutation();
  const duplicateSkill = useDuplicateSkillMutation();
  const { data: user } = useGetUser();
  const { agentStore } = useStore();
  const config = useMemo(() => getDraftConfigFromArgs(args), [args]);
  const sourceSkill = useGetSkill({
    orgId,
    skillId: config.sourceSkillId ?? "",
    enabled: requiresSourceSkill(config.operation) && !!config.sourceSkillId,
  });
  const visibilityOptions = useMemo(
    () =>
      getSkillVisibilityOptions({
        canCreateGlobal: isAdminUser(user),
        canCreateFormlyInternal: canCreateInternalSkill(user),
      }),
    [user]
  );
  const allowedVisibilities = useMemo(
    () => visibilityOptions.map((option) => option.value),
    [visibilityOptions]
  );
  const { data: currentThread } = useCurrentAgentThread();
  const initialValues = useMemo(
    () => getDraftValuesFromArgs(args, allowedVisibilities),
    [allowedVisibilities, args]
  );
  const toolCallId = toolResult?.tool_call_id ?? "";
  const isAlreadyApplied =
    !!toolCallId &&
    getAppliedToolCallIds(
      currentThread?.metadata as Record<string, any> | undefined
    ).includes(toolCallId);
  const [values, setValues] = useState<DraftSkillValues>(initialValues);
  const [status, setStatus] = useState<
    "unapplied" | "applying" | "applied" | "error"
  >(isAlreadyApplied ? "applied" : "unapplied");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [appliedSkillId, setAppliedSkillId] = useState<string | null>(null);

  // react-doctor-disable-next-line react-doctor/no-derived-state-effect, react-doctor/no-cascading-set-state -- A new tool call must reset the local editable draft and apply status together.
  useEffect(() => {
    setValues(initialValues);
    setStatus(isAlreadyApplied ? "applied" : "unapplied");
    setErrorMessage(null);
    setAppliedSkillId(null);
  }, [initialValues, isAlreadyApplied, toolCallId]);

  const hasToolResult = !!toolResult?.tool_call_id;
  const hasError = toolResult?.status === "error";
  const sourceAccessMessage = useMemo(() => {
    if (!requiresSourceSkill(config.operation)) {
      return null;
    }
    if (!config.sourceSkillId) {
      return "This draft is missing the source skill.";
    }
    if (
      sourceSkill.isLoading ||
      (sourceSkill.isFetching && !sourceSkill.data)
    ) {
      return "Checking source skill access.";
    }
    if (sourceSkill.isError || !sourceSkill.data) {
      return "Could not access the source skill.";
    }
    if (config.operation === "edit" && !sourceSkill.data.canEdit) {
      return "You cannot edit this skill with your current access. If you are not the creator, duplicate it to create an editable copy.";
    }
    return null;
  }, [
    config.operation,
    config.sourceSkillId,
    sourceSkill.data,
    sourceSkill.isError,
    sourceSkill.isFetching,
    sourceSkill.isLoading,
  ]);
  const applyBlocker =
    getValidationError(values, config) ?? sourceAccessMessage;
  const isLocked =
    status === "applied" ||
    createSkill.isPending ||
    patchSkill.isPending ||
    duplicateSkill.isPending ||
    status === "applying";
  const titlePrefix = titleByOperation[config.operation];
  const shouldHideDraftFields =
    config.operation === "edit" && !!sourceAccessMessage;
  const sourceAccessMessageColor =
    sourceAccessMessage === "Checking source skill access."
      ? "text.secondary"
      : "error";

  const trackAppliedToolCall = async (
    appliedToolCallId: string | undefined
  ) => {
    if (!appliedToolCallId || !agentStore.currentThreadId) {
      return;
    }

    await agentStore.client.threads.update(agentStore.currentThreadId, {
      metadata: {
        ...currentThread?.metadata,
        applied_tool_call_ids: [
          ...new Set([
            ...getAppliedToolCallIds(
              currentThread?.metadata as Record<string, any> | undefined
            ),
            appliedToolCallId,
          ]),
        ],
      },
    });
  };

  const handleApply = async () => {
    const nextError = getValidationError(values, config);
    if (nextError) {
      setErrorMessage(nextError);
      setStatus("error");
      return;
    }
    if (sourceAccessMessage) {
      setErrorMessage(sourceAccessMessage);
      setStatus("error");
      return;
    }

    setStatus("applying");
    setErrorMessage(null);

    try {
      const data: SkillWritePayload = {
        name: values.name.trim(),
        descriptionMd: values.descriptionMd.trim(),
        contentMd: values.contentMd.trim(),
        visibility: values.visibility,
      };
      const applied =
        config.operation === "edit"
          ? await patchSkill.mutateAsync({
              orgId,
              skillId: config.sourceSkillId ?? "",
              data,
            })
          : config.operation === "duplicate"
            ? await duplicateSkill.mutateAsync({
                orgId,
                skillId: config.sourceSkillId ?? "",
                data,
              })
            : await createSkill.mutateAsync({
                orgId,
                data,
              });

      await trackAppliedToolCall(toolResult?.tool_call_id);
      setAppliedSkillId(applied.id);
      setStatus("applied");
    } catch (caught) {
      setStatus("error");
      setErrorMessage(
        caught instanceof Error ? caught.message : "Could not apply skill."
      );
    }
  };

  const viewInSkills = () => {
    if (!appliedSkillId) {
      return;
    }

    navigate(
      `${fillRouteWithSlugs(ROUTES.SKILLS, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
      })}?skillId=${encodeURIComponent(appliedSkillId)}`
    );
  };

  return (
    <ToolCallFrame
      title={values.name ? `${titlePrefix}: ${values.name}` : titlePrefix}
      isLoading={isLoading && !hasToolResult}
      hasError={
        hasError ||
        status === "error" ||
        (shouldHideDraftFields && sourceAccessMessageColor === "error")
      }
      isExpandable={true}
      expandedByDefault={true}
      hasToolResult={hasToolResult}
      actions={
        shouldHideDraftFields ? null : (
          <>
            {status === "unapplied" || status === "error" ? (
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={
                  !(hasToolResult && !hasError && !applyBlocker && !isLocked)
                }
                onClick={handleApply}
              >
                Apply
              </Button>
            ) : null}
            {status === "applying" ? (
              <LoaderCircle className="size-5 animate-spin" />
            ) : null}
            {status === "applied" ? (
              <>
                <Button variant="text" color="info" size="small" disabled>
                  Applied
                </Button>
                {appliedSkillId ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={viewInSkills}
                  >
                    View in Skills
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        )
      }
    >
      {shouldHideDraftFields ? (
        <Typography color={sourceAccessMessageColor} variant="body2">
          {sourceAccessMessage}
        </Typography>
      ) : (
        <Stack gap={1.5}>
          {hasError ? (
            <Typography color="error" variant="body2">
              {typeof toolResult?.content === "string"
                ? toolResult.content
                : "Could not draft this skill."}
            </Typography>
          ) : null}

          <SkillFormFields
            values={values}
            onChange={setValues}
            canCreateGlobal={isAdminUser(user)}
            canCreateFormlyInternal={canCreateInternalSkill(user)}
            disabled={isLocked}
            nameError={!!values.name && !isSkillNameKebabCase(values.name)}
            nameHelperText={SKILL_NAME_KEBAB_CASE_HELPER_TEXT}
            namePlaceholder={null}
            descriptionPlaceholder={null}
            descriptionSize="small"
            contentLabel="Markdown content"
            contentPlaceholder={null}
            contentMinRows={8}
            includeUnavailableVisibilities={false}
            showSharedVisibilityWarning={false}
          />

          {applyBlocker && status !== "applied" ? (
            <Typography
              color={sourceAccessMessage ? "error" : "text.secondary"}
              variant="caption"
            >
              {applyBlocker}
            </Typography>
          ) : null}
          {errorMessage ? (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          ) : null}
        </Stack>
      )}
    </ToolCallFrame>
  );
};

export default DraftSkillTool;
